import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tag, Plus, Crown, Loader2, MapPin, Coins } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  "real-estate": "Real Estate",
  "vehicles": "Vehicles",
  "electronics": "Electronics",
  "services": "Services",
  "local-goods": "Local Goods",
  "jobs": "Jobs",
  "business": "Business",
  "other": "Other",
};

export default function Sellpages() {
  const email = getStoredEmail();
  const [listings, setListings] = useState([]);
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(true);
  const [sub, setSub] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try {
        const params = cat ? { category: cat } : {};
        const [l, s] = await Promise.all([
          axios.get(`${API}/sellpages`, { params }),
          email ? axios.get(`${API}/sellpages/subscription-status`, { params: { email } }) : Promise.resolve({ data: { active: false, price_usd: 24.95 } }),
        ]);
        if (!alive) return;
        setListings(l.data.items || []);
        setSub(s.data);
      } catch (e) { console.error(e); }
      finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, [cat, email]);

  const subscribe = async () => {
    if (!email) { alert("Subscribe to Newseze first (free) to start a Sellpages plan."); return; }
    setShowCheckout(true);
    try {
      const r = await axios.post(`${API}/checkout/subscription`, {
        email, tier: "sellpages", origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Checkout failed");
      setShowCheckout(false);
    }
  };

  return (
    <>
      <Header onOpenBrief={() => {}} />
      <main className="max-w-[1280px] mx-auto px-5 py-8" data-testid="sellpages-page">
        {/* Hero */}
        <section className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-6 mb-6" data-testid="sellpages-hero">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="max-w-[640px]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)] mb-1.5">Newseze Sellpages</div>
              <h1 className="text-[28px] sm:text-[34px] font-black tracking-[-0.03em] leading-tight">
                Sell anything, in front of Newseze&apos;s whole audience.
              </h1>
              <p className="text-[14px] mt-2 opacity-90">
                Unlimited classified listings, AI-polished titles, 30-day exposure, and readers
                <b className="text-[var(--gold)]"> earn credits for viewing your ad</b> — which means more eyeballs, more clicks.
              </p>
            </div>
            <div className="bg-white rounded-xl text-[var(--ink)] p-5 min-w-[260px]">
              <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--blue)]">Monthly</div>
              <div className="text-[36px] font-black tracking-[-0.03em] leading-none mt-1">${sub?.price_usd ?? "24.95"}<span className="text-[14px] text-[var(--muted)]">/mo</span></div>
              <div className="text-[11px] text-[var(--muted)] mt-1">All Pro benefits included</div>
              {sub?.active ? (
                <button className="w-full mt-3 btn gold" disabled data-testid="sellpages-active-badge">
                  <Crown size={14} /> Active subscriber
                </button>
              ) : (
                <button onClick={subscribe} disabled={showCheckout} className="w-full mt-3 btn gold" data-testid="sellpages-subscribe-btn">
                  {showCheckout ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />}
                  Start selling — ${sub?.price_usd ?? "24.95"}/mo
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Category filter + Post */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-1 flex-wrap" data-testid="sellpages-categories">
            <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.14em] ${!cat ? "bg-[var(--ink)] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)]"}`}>
              All
            </button>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <button key={k} onClick={() => setCat(k)} className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-[0.14em] ${cat === k ? "bg-[var(--ink)] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)]"}`} data-testid={`sellpages-cat-${k}`}>
                {v}
              </button>
            ))}
          </div>
          {sub?.active && (
            <button onClick={() => setShowForm(true)} className="btn" data-testid="sellpages-post-btn">
              <Plus size={14} /> Post a listing
            </button>
          )}
        </div>

        {/* Listings grid */}
        {busy ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[var(--blue)]" /></div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 text-[var(--muted)] italic" data-testid="sellpages-empty">
            No listings yet in this category. {sub?.active ? "Post the first one!" : "Be the first to subscribe and post."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sellpages-grid">
            {listings.map((l) => (
              <Link to={`/sellpages/${l.id}`} key={l.id} className="rounded-xl border border-[var(--line)] bg-white overflow-hidden hover:border-[var(--blue)] hover:-translate-y-0.5 transition group" data-testid={`sellpages-listing-${l.id}`}>
                {l.image_url && (
                  <div className="aspect-[16/10] bg-[var(--bg)] overflow-hidden">
                    <img src={l.image_url} alt={l.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">{CATEGORY_LABELS[l.category] || l.category}</span>
                    {l.price_usd != null && (
                      <span className="text-[10px] uppercase tracking-[0.14em] font-black bg-[var(--gold)] text-[#1d1303] px-1.5 py-0.5 rounded">${l.price_usd}</span>
                    )}
                  </div>
                  <div className="text-[15px] font-black text-[var(--ink)] leading-tight group-hover:text-[var(--blue)]">{l.title}</div>
                  <p className="text-[12.5px] text-[var(--muted)] mt-1 leading-snug line-clamp-2">{l.summary}</p>
                  <div className="flex items-center gap-2 mt-2.5 text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">
                    {l.location && (<><MapPin size={10} /><span>{l.location}</span><span>·</span></>)}
                    <Coins size={10} className="text-[var(--gold)]" /><span>view = +5 credits</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      {showForm && <PostListingModal onClose={() => setShowForm(false)} onPosted={(item) => { setListings([item, ...listings]); setShowForm(false); }} />}
      <Footer />
    </>
  );
}

function PostListingModal({ onClose, onPosted }) {
  const email = getStoredEmail();
  const [form, setForm] = useState({
    title: "", description: "", category: "other", price_usd: "", image_url: "", contact_email: email || "", location: "",
    // Vehicle-specific (Carfax-style) — only sent when category=vehicles
    vin: "", year: "", make: "", model: "", mileage: "", title_status: "clean", accidents: "none", owners: "", carfax_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isVehicle = form.category === "vehicles";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form, email };
      if (form.price_usd === "") delete payload.price_usd;
      else payload.price_usd = Number(form.price_usd);
      if (!form.image_url) delete payload.image_url;
      if (!form.location) delete payload.location;
      if (!form.contact_email) delete payload.contact_email;
      if (!isVehicle) {
        // Strip vehicle-only fields for non-vehicle listings
        ["vin", "year", "make", "model", "mileage", "title_status", "accidents", "owners", "carfax_url"].forEach((k) => delete payload[k]);
      } else {
        // Coerce numerics
        if (form.year) payload.year = Number(form.year);
        if (form.mileage) payload.mileage = Number(form.mileage);
        if (form.owners) payload.owners = Number(form.owners);
        // Build a vehicle_history object so backend can store it as a nested doc
        payload.vehicle_history = {
          vin: form.vin || null,
          year: form.year ? Number(form.year) : null,
          make: form.make || null,
          model: form.model || null,
          mileage: form.mileage ? Number(form.mileage) : null,
          title_status: form.title_status || null,
          accidents: form.accidents || null,
          owners: form.owners ? Number(form.owners) : null,
          carfax_url: form.carfax_url || null,
        };
      }
      const r = await axios.post(`${API}/sellpages/listings`, payload);
      onPosted(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not post listing.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-[640px] w-full p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="sellpages-post-modal">
        <h3 className="text-[22px] font-black tracking-[-0.02em] mb-3">Post a Sellpages listing</h3>
        <p className="text-[12px] text-[var(--muted)] mb-4">Newseze AI will polish your title for clarity. Listing stays live for 30 days.</p>
        {["title", "description", "image_url", "contact_email", "location"].map((k) => (
          <label key={k} className="block mb-3">
            <span className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--ink)]">{k.replace("_", " ")}</span>
            {k === "description" ? (
              <textarea required={k === "title" || k === "description"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={4} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid={`sellpages-field-${k}`} />
            ) : (
              <input required={k === "title"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid={`sellpages-field-${k}`} />
            )}
          </label>
        ))}
        <label className="block mb-3">
          <span className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--ink)]">Category</span>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-field-category">
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block mb-3">
          <span className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--ink)]">Price (USD, optional)</span>
          <input type="number" min="0" step="0.01" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-field-price" />
        </label>

        {/* Vehicle-specific Carfax-style intake — appears only for vehicles category */}
        {isVehicle && (
          <div className="mt-5 mb-4 rounded-lg border-2 border-[var(--gold)] bg-[#fff9ed] p-4" data-testid="sellpages-vehicle-section">
            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[#7a5b00] mb-2">Vehicle History (Carfax-style)</div>
            <p className="text-[11.5px] text-[var(--muted)] italic mb-3">Buyers trust vehicles with disclosed history. Newseze adds a verified-disclosure badge when these fields are completed.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">VIN (17 chars)</span>
                <input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} maxLength={17} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px] font-mono" data-testid="sellpages-vin" placeholder="1HGCM82633A123456" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Year</span>
                <input type="number" min="1900" max="2030" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-year" placeholder="2018" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Make</span>
                <input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-make" placeholder="Toyota" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Model</span>
                <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-model" placeholder="Camry" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Mileage</span>
                <input type="number" min="0" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-mileage" placeholder="68500" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Owners</span>
                <input type="number" min="0" max="20" value={form.owners} onChange={(e) => setForm({ ...form, owners: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-owners" placeholder="2" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Title status</span>
                <select value={form.title_status} onChange={(e) => setForm({ ...form, title_status: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-title-status">
                  <option value="clean">Clean</option>
                  <option value="salvage">Salvage</option>
                  <option value="rebuilt">Rebuilt</option>
                  <option value="lemon">Lemon</option>
                  <option value="lien">Lien on title</option>
                  <option value="unknown">Unknown</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Accident history</span>
                <select value={form.accidents} onChange={(e) => setForm({ ...form, accidents: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-accidents">
                  <option value="none">No reported accidents</option>
                  <option value="minor">Minor (cosmetic only)</option>
                  <option value="moderate">Moderate (panel/frame repair)</option>
                  <option value="major">Major (frame/airbag deploy)</option>
                  <option value="unknown">Unknown</option>
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--ink)]">Carfax / AutoCheck report URL (optional)</span>
                <input type="url" value={form.carfax_url} onChange={(e) => setForm({ ...form, carfax_url: e.target.value })} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="sellpages-carfax-url" placeholder="https://www.carfax.com/VehicleHistory/..." />
              </label>
            </div>
          </div>
        )}

        {err && <div className="text-[12px] text-rose-700 mb-3" data-testid="sellpages-post-error">{err}</div>}
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn">Cancel</button>
          <button type="submit" disabled={busy} className="btn gold" data-testid="sellpages-submit-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Tag size={14} />} Publish
          </button>
        </div>
      </form>
    </div>
  );
}
